import {
	memo,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
	type ComponentProps,
	type ReactNode,
	type RefObject,
} from 'react';

import { css, keyframes } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import Badge from '@atlaskit/badge-next';
import Button, { IconButton } from '@atlaskit/button-v25-3/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import AddIcon from '@atlaskit/icon/core/add';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import AutomationIcon from '@atlaskit/icon/core/automation';
import BranchIcon from '@atlaskit/icon/core/branch';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import IncidentIcon from '@atlaskit/icon/core/incident';
import RetryIcon from '@atlaskit/icon/core/retry';
import ShareIcon from '@atlaskit/icon/core/share';
import AiSearchIcon from '@atlaskit/icon-lab/core/ai-search';
import CompareIcon from '@atlaskit/icon-lab/core/compare';
import ServiceIcon from '@atlaskit/icon-lab/core/service';
import { BitbucketIcon, JiraIcon, JiraServiceManagementIcon } from '@atlaskit/logo';
import Lozenge from '@atlaskit/lozenge-next/new';
import { Text } from '@atlaskit/primitives-next';
import Skeleton from '@atlaskit/skeleton';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';

import {
	DEMO_CURSOR_FADE_MS,
	DEMO_CURSOR_TRAVEL_MS,
	JsmPlaygroundDemoCursor,
} from './components/jsm-playground-demo-cursor';
import { useNotifyDemoComplete } from './components/jsm-playground-demo-timeline';
import { JsmPlaygroundHeading } from './components/jsm-playground-heading';
import { JsmPlaygroundSkipAnimationButton } from './components/jsm-playground-skip-animation-button';
import {
	PaymentTeamAvatar,
	PriyaAvatar,
	RovoOpsMark,
	SlackMark,
	ZoomMark,
} from './jsm-playground-incident-marks';
import { jsmPortalPlaygroundMessages as messages } from './jsm-portal-playground-messages';

export type ItOperationsDemoState = 'autoplay' | 'initial' | 'analyzing' | 'complete';

type JsmPlaygroundItOperationsProps = {
	hasUserTakenOver: boolean;
	demoState?: ItOperationsDemoState;
	onDemoComplete?: () => void;
};

type CursorTarget = 'start' | 'rootCauseButton' | 'firstRootCause';
type RootCauseStatus = 'pending' | 'running' | 'done';

type RootCause = {
	title: string;
	confidence: string;
	confidenceAppearance: 'success' | 'moved' | 'default';
	reference: string;
	detail: string;
};

const ROOT_CAUSES: RootCause[] = [
	{
		title: 'Deployment',
		confidence: 'High',
		confidenceAppearance: 'success',
		reference: 'DEP-874',
		detail: 'Deployment to production 2 days ago',
	},
	{
		title: 'Feature flag',
		confidence: 'Medium',
		confidenceAppearance: 'moved',
		reference: 'FF-678',
		detail: 'Deployed to production 8 days ago',
	},
	{
		title: 'Deployment',
		confidence: 'Low',
		confidenceAppearance: 'default',
		reference: 'DEP-456',
		detail: 'Deployed to production 8 days ago',
	},
];

const ROOT_CAUSE_STEPS = [
	{ id: 'deployments', width: 260 },
	{ id: 'feature-flags', width: 220 },
	{ id: 'pairing', width: 380 },
] as const;

const ROOT_CAUSE_STEP_DURATION = 1400;
const ROOT_CAUSE_REVEAL_PAUSE = 1400;
const ROOT_CAUSE_ANALYSIS_DURATION =
	ROOT_CAUSE_STEPS.length * ROOT_CAUSE_STEP_DURATION + ROOT_CAUSE_REVEAL_PAUSE;
const PANEL_ENTER_DURATION_MS = 440;
const PANEL_GROW_DURATION_MS = 480;
const PRIYA_CLICK_AT = 650 + DEMO_CURSOR_TRAVEL_MS + 550;
const PRIYA_RETARGET_AT = PRIYA_CLICK_AT + ROOT_CAUSE_ANALYSIS_DURATION + 200;

export const IT_OPERATIONS_TIMELINE = {
	cursorIn: 250,
	cursorToButton: 650,
	buttonPress: 650 + DEMO_CURSOR_TRAVEL_MS + 300,
	click: PRIYA_CLICK_AT,
	cursorOutAfterClick: PRIYA_CLICK_AT + 150,
	retargetFirstCause: PRIYA_RETARGET_AT,
	cursorToFirstCause: PRIYA_RETARGET_AT + DEMO_CURSOR_TRAVEL_MS + 200,
	firstCausePress: PRIYA_RETARGET_AT + DEMO_CURSOR_TRAVEL_MS + 500,
	selectFirstCause: PRIYA_RETARGET_AT + DEMO_CURSOR_TRAVEL_MS + 750,
	insightIn: PRIYA_RETARGET_AT + DEMO_CURSOR_TRAVEL_MS + 1150,
	cursorOut: PRIYA_RETARGET_AT + DEMO_CURSOR_TRAVEL_MS + 1550,
} as const;

const noop = () => {};

const STATUS_TRANSITIONS = [
	{ message: messages.investigating, appearance: 'inprogress' as const },
	{ message: messages.monitoring, appearance: 'inprogress' as const },
	{ message: messages.resolved, appearance: 'success' as const },
];

type ButtonIcon = NonNullable<ComponentProps<typeof Button>['iconBefore']>;
type IconButtonIcon = NonNullable<ComponentProps<typeof IconButton>['icon']>;

const AddButtonIcon: IconButtonIcon = () => <AddIcon label="" size="medium" />;
const AutomationButtonIcon: IconButtonIcon = () => <AutomationIcon label="" size="medium" />;
const ChevronDownIconButton: IconButtonIcon = () => <ChevronDownIcon label="" size="medium" />;
const ChevronUpIconButton: IconButtonIcon = () => <ChevronUpIcon label="" size="medium" />;
const RetryIconButton: IconButtonIcon = () => <RetryIcon label="" size="medium" />;
const BranchButtonIcon: ButtonIcon = () => <BranchIcon label="" size="medium" />;
const AiSearchButtonIcon: ButtonIcon = () => <AiSearchIcon label="" size="medium" />;
const ChevronDownButtonIcon: ButtonIcon = () => <ChevronDownIcon label="" size="medium" />;
// IconTile's orange appearance fills the square, and its size is a fixed box, so
// padding on that box never shows around the cone. Draw the grey tile ourselves.
const incidentIconTileStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	boxSizing: 'border-box',
	backgroundColor: token('color.background.neutral'),
	color: token('color.icon.accent.orange'),
	borderRadius: token('radius.large'),
});

const incidentIconTileMediumStyles = css({
	width: token('space.400'),
	height: token('space.400'),
	padding: token('space.100'),
});

const incidentIconTileSmallStyles = css({
	width: '26px',
	height: '26px',
});

const IncidentIconTile = ({ size }: { size: 'small' | 'medium' }) => (
	<span
		css={[
			incidentIconTileStyles,
			size === 'medium' ? incidentIconTileMediumStyles : incidentIconTileSmallStyles,
		]}
	>
		<IncidentIcon size={size === 'medium' ? 'medium' : 'small'} label="" />
	</span>
);

const usePrefersReducedMotion = () => {
	const [prefersReducedMotion, setPrefersReducedMotion] = useState(
		() =>
			typeof window !== 'undefined' &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches,
	);

	useEffect(() => {
		const query = window.matchMedia('(prefers-reduced-motion: reduce)');
		const update = () => setPrefersReducedMotion(query.matches);
		update();
		query.addEventListener('change', update);
		return () => query.removeEventListener('change', update);
	}, []);

	return prefersReducedMotion;
};

const JsmPlaygroundItOperationsComponent = ({
	hasUserTakenOver,
	demoState = 'autoplay',
	onDemoComplete,
}: JsmPlaygroundItOperationsProps) => {
	const intl = useIntl();
	const prefersReducedMotion = usePrefersReducedMotion();
	const notifyDemoComplete = useNotifyDemoComplete(onDemoComplete);
	const shouldStartComplete = demoState === 'complete' || prefersReducedMotion;
	const shouldStartAnalyzing = demoState === 'analyzing';
	const shouldAutoplay = demoState === 'autoplay' && !prefersReducedMotion;

	const [isDescriptionOpen, setIsDescriptionOpen] = useState(
		!shouldStartComplete && !shouldStartAnalyzing,
	);
	const [isRovoExpanded, setIsRovoExpanded] = useState(shouldStartComplete || shouldStartAnalyzing);
	const [isRovoResolved, setIsRovoResolved] = useState(shouldStartComplete);
	const [selectedCauseIndex, setSelectedCauseIndex] = useState<number | null>(
		shouldStartComplete ? 0 : null,
	);
	const [analysisRun, setAnalysisRun] = useState({
		runningIndex: shouldStartAnalyzing ? 0 : (null as number | null),
		doneCount: 0,
	});
	const [isAnimationComplete, setIsAnimationComplete] = useState(
		shouldStartComplete || demoState === 'initial',
	);
	const [cursor, setCursor] = useState({
		target: 'start' as CursorTarget,
		isVisible: false,
		isPressed: false,
	});
	const [isCursorTransitionEnabled, setIsCursorTransitionEnabled] = useState(false);
	const [isFirstCauseHighlighted, setIsFirstCauseHighlighted] = useState(false);
	const [cursorStops, setCursorStops] = useState<Record<CursorTarget, { x: number; y: number }>>({
		start: { x: 0, y: 0 },
		rootCauseButton: { x: 0, y: 0 },
		firstRootCause: { x: 0, y: 0 },
	});

	const bodyRef = useRef<HTMLDivElement>(null);
	const rootCauseButtonRef = useRef<HTMLSpanElement>(null);
	const firstRootCauseRef = useRef<HTMLButtonElement>(null);
	const hasUserTakenOverRef = useRef(hasUserTakenOver);
	hasUserTakenOverRef.current = hasUserTakenOver;

	const measureCursorStops = useCallback(() => {
		const body = bodyRef.current;
		if (!body) {
			return;
		}

		const bodyBounds = body.getBoundingClientRect();
		const buttonBounds = rootCauseButtonRef.current?.getBoundingClientRect();
		const firstCauseBounds = firstRootCauseRef.current?.getBoundingClientRect();

		setCursorStops((previous) => ({
			start: buttonBounds
				? {
						x: bodyBounds.width * 0.5 - 150,
						y: bodyBounds.height * 0.65,
					}
				: previous.start,
			rootCauseButton: buttonBounds
				? {
						x: buttonBounds.left - bodyBounds.left + 16,
						y: buttonBounds.top - bodyBounds.top + buttonBounds.height / 2,
					}
				: previous.rootCauseButton,
			firstRootCause: firstCauseBounds
				? {
						x: firstCauseBounds.left - bodyBounds.left + 96,
						y: firstCauseBounds.top - bodyBounds.top + 16,
					}
				: previous.firstRootCause,
		}));
	}, []);

	useLayoutEffect(() => {
		measureCursorStops();
		window.addEventListener('resize', measureCursorStops);
		return () => window.removeEventListener('resize', measureCursorStops);
	}, [measureCursorStops, isRovoExpanded, isRovoResolved]);

	useEffect(() => {
		setIsCursorTransitionEnabled(true);
	}, []);

	useEffect(() => {
		if (hasUserTakenOver) {
			setIsFirstCauseHighlighted(false);
		}
	}, [hasUserTakenOver]);

	useEffect(() => {
		if (shouldStartComplete) {
			notifyDemoComplete();
		}
	}, [notifyDemoComplete, shouldStartComplete]);

	useEffect(() => {
		if (!shouldAutoplay) {
			return;
		}

		const timer = window.setTimeout(() => {
			if (!hasUserTakenOverRef.current) {
				notifyDemoComplete();
			}
		}, IT_OPERATIONS_TIMELINE.cursorOut + DEMO_CURSOR_FADE_MS);
		return () => window.clearTimeout(timer);
	}, [notifyDemoComplete, shouldAutoplay]);

	const startRovoAnalysis = useCallback(() => {
		setIsDescriptionOpen(false);
		setIsRovoExpanded(true);
		setIsRovoResolved(false);
		setSelectedCauseIndex(null);
	}, []);

	const toggleRovo = (isExpanded: boolean) => {
		setIsRovoExpanded(isExpanded);
		if (isExpanded) {
			setIsDescriptionOpen(false);
		}
	};

	useEffect(() => {
		if (!isRovoExpanded || isRovoResolved || demoState === 'analyzing') {
			return;
		}

		const timers: number[] = [];
		ROOT_CAUSE_STEPS.forEach((_, index) => {
			timers.push(
				window.setTimeout(
					() => setAnalysisRun((previous) => ({ ...previous, runningIndex: index })),
					index * ROOT_CAUSE_STEP_DURATION,
				),
			);
			timers.push(
				window.setTimeout(
					() => setAnalysisRun({ runningIndex: index, doneCount: index + 1 }),
					(index + 1) * ROOT_CAUSE_STEP_DURATION - 20,
				),
			);
		});
		timers.push(
			window.setTimeout(() => {
				setAnalysisRun({ runningIndex: null, doneCount: ROOT_CAUSE_STEPS.length });
			}, ROOT_CAUSE_STEPS.length * ROOT_CAUSE_STEP_DURATION),
		);
		timers.push(window.setTimeout(() => setIsRovoResolved(true), ROOT_CAUSE_ANALYSIS_DURATION));

		return () => timers.forEach(window.clearTimeout);
	}, [demoState, isRovoExpanded, isRovoResolved]);

	useEffect(() => {
		if (!shouldAutoplay) {
			return;
		}

		const timers: number[] = [];
		const at = (ms: number, run: () => void) => {
			timers.push(
				window.setTimeout(() => {
					if (!hasUserTakenOverRef.current) {
						run();
					}
				}, ms),
			);
		};
		const updateCursor = (next: Partial<typeof cursor>) =>
			setCursor((previous) => ({ ...previous, ...next }));

		at(IT_OPERATIONS_TIMELINE.cursorIn, () => updateCursor({ target: 'start', isVisible: true }));
		at(IT_OPERATIONS_TIMELINE.cursorToButton, () => updateCursor({ target: 'rootCauseButton' }));
		at(IT_OPERATIONS_TIMELINE.buttonPress, () => updateCursor({ isPressed: true }));
		at(IT_OPERATIONS_TIMELINE.click, () => {
			updateCursor({ isPressed: false });
			startRovoAnalysis();
		});
		at(IT_OPERATIONS_TIMELINE.cursorOutAfterClick, () => updateCursor({ isVisible: false }));
		at(IT_OPERATIONS_TIMELINE.retargetFirstCause, () => {
			measureCursorStops();
			updateCursor({ target: 'firstRootCause' });
		});
		at(IT_OPERATIONS_TIMELINE.cursorToFirstCause, () => {
			updateCursor({ isVisible: true });
			setIsFirstCauseHighlighted(true);
		});
		at(IT_OPERATIONS_TIMELINE.firstCausePress, () => updateCursor({ isPressed: true }));
		at(IT_OPERATIONS_TIMELINE.selectFirstCause, () => updateCursor({ isPressed: false }));
		at(IT_OPERATIONS_TIMELINE.insightIn, () => setSelectedCauseIndex(0));
		at(IT_OPERATIONS_TIMELINE.cursorOut, () => {
			updateCursor({ isVisible: false });
			setIsFirstCauseHighlighted(false);
			setIsAnimationComplete(true);
		});

		return () => timers.forEach(window.clearTimeout);
	}, [measureCursorStops, shouldAutoplay, startRovoAnalysis]);

	const skipAnimation = () => {
		setCursor((previous) => ({ ...previous, isVisible: false, isPressed: false }));
		setIsDescriptionOpen(false);
		setIsRovoExpanded(true);
		setAnalysisRun({ runningIndex: null, doneCount: ROOT_CAUSE_STEPS.length });
		setIsRovoResolved(true);
		setSelectedCauseIndex(0);
		setIsFirstCauseHighlighted(false);
		setIsAnimationComplete(true);
	};

	return (
		<div ref={bodyRef} css={pageStyles} data-testid="it-operations-preview">
			<div css={leftColumnStyles}>
				<WorkItemHeader />
				<div css={leftCardsStyles}>
					<DescriptionCard
						isExpanded={isDescriptionOpen}
						onToggle={() => setIsDescriptionOpen((previous) => !previous)}
					/>
					<RovoOpsCard
						firstRootCauseRef={firstRootCauseRef}
						isFirstCauseHighlighted={isFirstCauseHighlighted}
						isExpanded={isRovoExpanded}
						isResolved={isRovoResolved}
						onIdentify={startRovoAnalysis}
						onSelectCause={setSelectedCauseIndex}
						onToggle={toggleRovo}
						shouldReduceMotion={prefersReducedMotion}
						rootCauseButtonRef={rootCauseButtonRef}
						run={analysisRun}
						selectedCauseIndex={selectedCauseIndex}
					/>
				</div>
			</div>

			<div css={rightColumnStyles}>
				<StatusRow />
				<DetailsPanel />
			</div>

			<JsmPlaygroundDemoCursor
				label="Priya"
				hasUserTakenOver={hasUserTakenOver}
				isPressed={cursor.isPressed}
				isTransitionEnabled={isCursorTransitionEnabled}
				isVisible={cursor.isVisible}
				x={cursorStops[cursor.target].x}
				y={cursorStops[cursor.target].y}
			/>

			<JsmPlaygroundSkipAnimationButton
				isVisible={!isAnimationComplete && !hasUserTakenOver}
				label={intl.formatMessage(messages.skipAnimation)}
				onClick={skipAnimation}
				placement="end"
			/>
		</div>
	);
};

// Memoised so the playground root's cursor state changes (enter, hover, press, leave) do not
// re-render this subtree. Its props are primitives plus a stable callback.
export const JsmPlaygroundItOperations = memo(JsmPlaygroundItOperationsComponent);

const WorkItemHeader = () => {
	const intl = useIntl();
	return (
		<div css={workItemHeaderStyles}>
			<div css={workItemTitleStyles}>
				<IncidentIconTile size="medium" />
				<JsmPlaygroundHeading level={1} size="large">
					{intl.formatMessage(messages.incidentTitle)}
				</JsmPlaygroundHeading>
			</div>
			<span aria-hidden="true" css={inertControlStyles}>
				<IconButton
					icon={AddButtonIcon}
					label={intl.formatMessage(messages.addToWorkItem)}
					onClick={noop}
					tabIndex={-1}
				/>
			</span>
		</div>
	);
};

const WorkItemCard = ({ children }: { children: ReactNode }) => (
	<div css={workItemCardStyles}>{children}</div>
);

const CardHeader = ({
	title,
	elemBefore,
	action,
}: {
	title: string;
	elemBefore?: ReactNode;
	action?: ReactNode;
}) => (
	<div css={cardHeaderStyles}>
		<div css={cardHeaderTitleStyles}>
			{elemBefore}
			<Text color="color.text.subtle" weight="semibold">
				{title}
			</Text>
		</div>
		{action}
	</div>
);

const AlertSmartLink = () => (
	<span aria-label="P1 Payment – High Error Rate, 172 alerts" css={alertSmartLinkStyles}>
		<span css={roundedBadgeStyles}>
			<Badge appearance="danger">P1</Badge>
		</span>
		<Text size="small">Payment – High Error Rate</Text>
		<span css={roundedBadgeStyles}>
			<Badge appearance="neutral">x172</Badge>
		</span>
	</span>
);

const EntityChip = ({ children, icon }: { children: ReactNode; icon: ReactNode }) => (
	<span css={entityChipStyles}>
		{icon}
		<Text size="small">{children}</Text>
	</span>
);

const paymentApiIconTileStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	padding: '4px',
	backgroundColor: token('color.background.accent.purple.subtle'),
	color: token('color.icon'),
	borderRadius: token('radius.medium'),
	lineHeight: 0,
});

const PaymentApiIconTile = () => (
	<span css={paymentApiIconTileStyles}>
		<ServiceIcon size="small" label="" />
	</span>
);

const paymentServiceChipIconStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	boxSizing: 'border-box',
	width: token('space.200'),
	height: token('space.200'),
	backgroundColor: token('color.background.accent.purple.subtle'),
	color: token('color.icon'),
	borderRadius: token('radius.small'),
	lineHeight: 0,
	// Core icons only honour small (12px) and medium (16px). Any other size falls back to medium.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& svg': {
		width: '10px',
		height: '10px',
	},
});

const PaymentServiceChipIcon = () => (
	<span css={paymentServiceChipIconStyles}>
		<ServiceIcon label="" size="small" />
	</span>
);

const DescriptionCard = ({
	isExpanded,
	onToggle,
}: {
	isExpanded: boolean;
	onToggle: () => void;
}) => {
	const intl = useIntl();
	const bodyRef = useRef<HTMLDivElement>(null);
	const [bodyHeight, setBodyHeight] = useState<number>();

	useLayoutEffect(() => {
		if (bodyRef.current) {
			setBodyHeight(bodyRef.current.scrollHeight);
		}
	}, []);

	return (
		<WorkItemCard>
			<div css={descriptionCardContentStyles}>
				<CardHeader
					title={intl.formatMessage(messages.description)}
					action={
						<IconButton
							appearance="subtle"
							icon={isExpanded ? ChevronUpIconButton : ChevronDownIconButton}
							label={intl.formatMessage(
								isExpanded ? messages.collapseDescription : messages.expandDescription,
							)}
							onClick={onToggle}
							spacing="compact"
						/>
					}
				/>
				<div
					ref={bodyRef}
					aria-hidden={!isExpanded}
					css={[descriptionDisclosureStyles, isExpanded && descriptionDisclosureExpandedStyles]}
					style={{ maxHeight: isExpanded ? bodyHeight : 0 }}
				>
					<div css={descriptionBodyStyles}>
						<p css={paragraphStyles}>
							<Text>{intl.formatMessage(messages.incidentDescriptionStart)} </Text>
							<AlertSmartLink />{' '}
							<Text>
								<FormattedMessage
									{...messages.incidentDescriptionEnd}
									values={{
										service: (chunks) => <strong>{chunks}</strong>,
									}}
								/>
							</Text>
						</p>
						<div css={impactedServiceSectionStyles}>
							<Text color="color.text.subtle" weight="semibold">
								{intl.formatMessage(messages.impactedService)}
							</Text>
							<div css={impactedServiceRowStyles}>
								<div css={entityValueStyles}>
									<PaymentApiIconTile />
									<Text weight="medium">{intl.formatMessage(messages.paymentApi)}</Text>
								</div>
								<div css={onCallStyles}>
									<Text color="color.text.subtlest" size="small">
										{intl.formatMessage(messages.onCall)}
									</Text>
									<PriyaAvatar size={16} />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</WorkItemCard>
	);
};

type RovoOpsCardProps = {
	isExpanded: boolean;
	isResolved: boolean;
	run: { runningIndex: number | null; doneCount: number };
	selectedCauseIndex: number | null;
	onIdentify: () => void;
	onSelectCause: (index: number | null) => void;
	onToggle: (isExpanded: boolean) => void;
	shouldReduceMotion: boolean;
	rootCauseButtonRef: RefObject<HTMLSpanElement>;
	firstRootCauseRef: RefObject<HTMLButtonElement>;
	isFirstCauseHighlighted: boolean;
};

const RovoOpsCard = ({
	isExpanded,
	isResolved,
	run,
	selectedCauseIndex,
	onIdentify,
	onSelectCause,
	onToggle,
	shouldReduceMotion,
	rootCauseButtonRef,
	firstRootCauseRef,
	isFirstCauseHighlighted,
}: RovoOpsCardProps) => {
	const intl = useIntl();
	const isAnalyzing = isExpanded && !isResolved;
	const bodyRef = useRef<HTMLDivElement>(null);
	const hasMeasuredBodyRef = useRef(false);

	useLayoutEffect(() => {
		const body = bodyRef.current;
		if (!body) {
			return;
		}

		const applyMeasuredHeight = () => {
			body.style.setProperty('--rovo-panel-max-height', `${body.scrollHeight}px`);
			hasMeasuredBodyRef.current = true;
		};

		if (!hasMeasuredBodyRef.current || shouldReduceMotion) {
			applyMeasuredHeight();
			return;
		}

		const timer = window.setTimeout(applyMeasuredHeight, 30);
		return () => window.clearTimeout(timer);
	}, [isAnalyzing, isExpanded, isResolved, selectedCauseIndex, shouldReduceMotion]);

	return (
		<div css={rovoCardFrameStyles}>
			<div css={[rovoGlowStyles, isAnalyzing && rovoGlowVisibleStyles]} />
			<div css={rovoRingStyles}>
				<div css={[rovoRingBitmapStyles, isAnalyzing && rovoRingVisibleStyles]} />
			</div>
			<div css={rovoCardContentStyles}>
				<WorkItemCard>
					<div ref={bodyRef} css={rovoDisclosureStyles}>
						{!isExpanded && isResolved ? (
							<CardHeader
								title={intl.formatMessage(messages.rovoOpsTitle)}
								elemBefore={
									<span css={rovoMarkWrapperStyles}>
										<RovoOpsMark label="Rovo Ops" />
									</span>
								}
								action={
									<IconButton
										appearance="subtle"
										icon={ChevronDownIconButton}
										label={intl.formatMessage(messages.expandRovoOps)}
										onClick={() => onToggle(true)}
										spacing="compact"
									/>
								}
							/>
						) : !isExpanded ? (
							<div css={rovoInitialStyles}>
								<span css={rovoMarkWrapperStyles}>
									<RovoOpsMark label="Rovo Ops" />
								</span>
								<div css={rovoInitialContentStyles}>
									<div css={rovoCopyStyles}>
										<Text color="color.text.subtle" weight="semibold">
											{intl.formatMessage(messages.rovoOpsTitle)}
										</Text>
										<Text color="color.text.subtlest">
											{intl.formatMessage(messages.rovoOpsDescription)}
										</Text>
									</div>
									<div css={rovoActionsStyles}>
										<span ref={rootCauseButtonRef} css={inlineControlStyles}>
											<Button iconBefore={BranchButtonIcon} onClick={onIdentify} spacing="compact">
												{intl.formatMessage(messages.identifyRootCauses)}
											</Button>
										</span>
										<span aria-hidden="true" css={inertControlStyles}>
											<Button
												iconBefore={AiSearchButtonIcon}
												onClick={noop}
												spacing="compact"
												tabIndex={-1}
											>
												{intl.formatMessage(messages.findSimilarIncidents)}
											</Button>
										</span>
									</div>
								</div>
							</div>
						) : (
							<div css={rovoExpandedStyles}>
								<CardHeader
									title={intl.formatMessage(messages.rovoOpsTitle)}
									elemBefore={
										<span css={rovoMarkWrapperStyles}>
											<RovoOpsMark label="Rovo Ops" />
										</span>
									}
									action={
										<IconButton
											appearance="subtle"
											icon={ChevronUpIconButton}
											label={intl.formatMessage(messages.collapseRovoOps)}
											onClick={() => onToggle(false)}
											spacing="compact"
										/>
									}
								/>
								<div css={rootCausePanelStyles}>
									<div css={rootCausePanelHeaderStyles}>
										<Text color="color.text.subtle" size="small" weight="semibold">
											{intl.formatMessage(messages.likelyRootCauses)}
										</Text>
										{isResolved && (
											<span aria-hidden="true" css={inertControlStyles}>
												<IconButton
													appearance="subtle"
													icon={RetryIconButton}
													label={intl.formatMessage(messages.regenerateRootCauses)}
													onClick={noop}
													spacing="compact"
													tabIndex={-1}
												/>
											</span>
										)}
									</div>
									{isResolved ? (
										<ResolvedRootCauses
											firstRootCauseRef={firstRootCauseRef}
											isFirstCauseHighlighted={isFirstCauseHighlighted}
											onSelectCause={onSelectCause}
											selectedCauseIndex={selectedCauseIndex}
										/>
									) : (
										<div css={rootCauseStepsStyles}>
											{ROOT_CAUSE_STEPS.map((step, index) => (
												<RootCauseStep
													key={step.id}
													id={step.id}
													isLast={index === ROOT_CAUSE_STEPS.length - 1}
													status={
														index < run.doneCount
															? 'done'
															: index === run.runningIndex
																? 'running'
																: 'pending'
													}
													width={step.width}
												/>
											))}
										</div>
									)}
								</div>
								<span aria-hidden="true" css={inertControlStyles}>
									<Button
										iconBefore={AiSearchButtonIcon}
										onClick={noop}
										spacing="compact"
										tabIndex={-1}
									>
										{intl.formatMessage(messages.findSimilarIncidents)}
									</Button>
								</span>
							</div>
						)}
					</div>
				</WorkItemCard>
			</div>
		</div>
	);
};

const BitbucketChipIcon = () => (
	<BitbucketIcon appearance="brand" label="" shouldUseNewLogoDesign size="xxsmall" />
);

const RootCauseStepCopy = ({ id }: { id: (typeof ROOT_CAUSE_STEPS)[number]['id'] }) => {
	if (id === 'deployments') {
		return (
			<>
				<Text size="small">Searching recent</Text>
				<EntityChip icon={<BitbucketChipIcon />}>Payment API</EntityChip>
				<Text size="small">deployments</Text>
			</>
		);
	}
	if (id === 'feature-flags') {
		return (
			<>
				<Text size="small">Scanning feature flags</Text>
				<EntityChip icon={<BitbucketChipIcon />}>Payment FF</EntityChip>
			</>
		);
	}
	return (
		<>
			<Text size="small">Finding likely root causes and pairing with recent</Text>
			<EntityChip icon={<CompareIcon color={token('color.icon.subtle')} label="" />}>
				New Payment
			</EntityChip>
			<Text size="small">release</Text>
		</>
	);
};

const RootCauseStep = ({
	id,
	status,
	width,
	isLast,
}: {
	id: (typeof ROOT_CAUSE_STEPS)[number]['id'];
	status: RootCauseStatus;
	width: number;
	isLast: boolean;
}) => (
	<div css={rootCauseStepStyles}>
		<div css={stepRailColumnStyles}>
			{status === 'done' ? (
				<CheckCircleIcon color={token('color.icon.success')} label="Done" />
			) : (
				<span
					aria-label={status === 'running' ? 'In progress' : 'Not started'}
					css={[pendingStepStyles, status === 'running' && runningStepStyles]}
					role="img"
				/>
			)}
			{!isLast && <span css={stepRailStyles} />}
		</div>
		<div css={stepCopyStyles}>
			{status === 'pending' ? (
				<Skeleton borderRadius={token('radius.small')} height={12} width={width} />
			) : (
				<RootCauseStepCopy id={id} />
			)}
		</div>
	</div>
);

const ResolvedRootCauses = ({
	selectedCauseIndex,
	onSelectCause,
	firstRootCauseRef,
	isFirstCauseHighlighted,
}: {
	selectedCauseIndex: number | null;
	onSelectCause: (index: number | null) => void;
	firstRootCauseRef: RefObject<HTMLButtonElement>;
	isFirstCauseHighlighted: boolean;
}) => {
	const intl = useIntl();
	return (
		<div css={resolvedContentStyles}>
			<div css={rootCauseCardsStyles}>
				{ROOT_CAUSES.map((cause, index) => (
					<button
						key={cause.reference}
						ref={index === 0 ? firstRootCauseRef : undefined}
						aria-label={`${cause.title}, ${cause.confidence} confidence, ${cause.reference}: ${cause.detail}`}
						aria-pressed={selectedCauseIndex === index}
						css={[
							rootCauseCardStyles,
							index < ROOT_CAUSES.length - 1 && rootCauseCardDividerStyles,
							selectedCauseIndex === index && selectedRootCauseCardStyles,
							index === 0 && isFirstCauseHighlighted && highlightedRootCauseCardStyles,
						]}
						onClick={() => onSelectCause(selectedCauseIndex === index ? null : index)}
						type="button"
					>
						<div css={rootCauseTitleStyles}>
							<Text size="small" weight="semibold">
								{cause.title}
							</Text>
							<Lozenge appearance={cause.confidenceAppearance}>{cause.confidence}</Lozenge>
						</div>
						<span css={referenceTagStyles}>
							<ShareIcon color={token('color.icon.subtle')} label="" size="medium" />
							<Text size="small">{cause.reference}</Text>
						</span>
						<Text size="small">{cause.detail}</Text>
					</button>
				))}
			</div>

			{selectedCauseIndex !== null && <RootCauseInsight index={selectedCauseIndex} />}

			<div css={sourcesStyles}>
				<div css={sourceLogosStyles}>
					<span css={sourceLogoStyles}>
						<BitbucketIcon appearance="brand" label="" shouldUseNewLogoDesign size="xsmall" />
					</span>
					<span css={[sourceLogoStyles, sourceLogoOverlapStyles]}>
						<JiraIcon appearance="brand" label="" shouldUseNewLogoDesign size="xsmall" />
					</span>
					<span css={[sourceLogoStyles, sourceLogoOverlapStyles]}>
						<JiraServiceManagementIcon
							appearance="brand"
							label=""
							shouldUseNewLogoDesign
							size="xsmall"
						/>
					</span>
				</div>
				<Text color="color.text.subtle" weight="medium">
					{intl.formatMessage(messages.sources)}
				</Text>
			</div>
		</div>
	);
};

const RootCauseInsight = ({ index }: { index: number }) => {
	const appearance = ROOT_CAUSES[index]?.confidenceAppearance;
	return (
		<div
			css={[
				rootCauseInsightStyles,
				appearance === 'moved' && warningInsightStyles,
				appearance === 'default' && neutralInsightStyles,
			]}
		>
			<p css={paragraphStyles}>
				{index === 0 ? (
					<>
						<Text weight="semibold">Likely cause of outage.</Text>{' '}
						<Text>There are patterns of recent alerts that have similar root causes of </Text>
						<EntityChip icon={<PaymentServiceChipIcon />}>Payment API</EntityChip>
						<Text>. This was most likely caused by the recent deployment </Text>
						<EntityChip
							icon={
								<BitbucketIcon appearance="brand" label="" shouldUseNewLogoDesign size="xxsmall" />
							}
						>
							Update payment
						</EntityChip>{' '}
						<Text>2 days ago</Text>
					</>
				) : index === 1 ? (
					<>
						<Text weight="semibold">Possible contributing factor.</Text>{' '}
						<Text>The feature flag </Text>
						<EntityChip
							icon={
								<BitbucketIcon appearance="brand" label="" shouldUseNewLogoDesign size="xxsmall" />
							}
						>
							Payment FF
						</EntityChip>{' '}
						<Text>was enabled on </Text>
						<EntityChip icon={<PaymentServiceChipIcon />}>Payment API</EntityChip>{' '}
						<Text>
							8 days ago and touches a related code path, though its rollout window is less closely
							aligned with the alert spike than the deployment above.
						</Text>
					</>
				) : (
					<>
						<Text weight="semibold">Unlikely, but not ruled out.</Text> <Text>The deployment </Text>
						<EntityChip
							icon={
								<BitbucketIcon appearance="brand" label="" shouldUseNewLogoDesign size="xxsmall" />
							}
						>
							Payment retries
						</EntityChip>{' '}
						<Text>
							shipped 8 days ago and touches this service, but its timing correlates weakly with the
							alert spike compared to the more recent changes above.
						</Text>
					</>
				)}
			</p>
		</div>
	);
};

const StatusRow = () => {
	const intl = useIntl();

	return (
		<div css={statusRowStyles}>
			<div css={statusDropdownStyles}>
				<DropdownMenu<HTMLButtonElement>
					placement="bottom-start"
					shouldRenderToParent
					testId="jsm-it-operations-status"
					trigger={({ triggerRef, ...triggerProps }) => (
						<Button {...triggerProps} ref={triggerRef} iconAfter={ChevronDownButtonIcon}>
							{intl.formatMessage(messages.open)}
						</Button>
					)}
				>
					<div css={statusMenuContentStyles}>
						<DropdownItemGroup>
							{STATUS_TRANSITIONS.map((transition) => (
								<DropdownItem
									key={transition.message.id}
									elemAfter={
										<span css={statusTransitionStyles}>
											<ArrowRightIcon color={token('color.icon.subtle')} label="" size="small" />
											<Lozenge appearance={transition.appearance}>
												{intl.formatMessage(transition.message)}
											</Lozenge>
										</span>
									}
									onClick={noop}
								>
									{intl.formatMessage(transition.message)}
								</DropdownItem>
							))}
						</DropdownItemGroup>
					</div>
				</DropdownMenu>
			</div>
			<span aria-hidden="true" css={inertControlStyles}>
				<IconButton
					icon={AutomationButtonIcon}
					label={intl.formatMessage(messages.runAutomation)}
					onClick={noop}
					tabIndex={-1}
				/>
			</span>
		</div>
	);
};

const DetailsPanel = () => {
	const intl = useIntl();
	const [isMajorIncident, setIsMajorIncident] = useState(true);

	return (
		<div css={detailsPanelStyles}>
			<div css={detailsHeaderStyles}>
				<JsmPlaygroundHeading level={2} size="xsmall">
					{intl.formatMessage(messages.details)}
				</JsmPlaygroundHeading>
				<span aria-hidden="true" css={inertControlStyles}>
					<IconButton
						appearance="subtle"
						icon={ChevronUpIconButton}
						label={intl.formatMessage(messages.collapseDetails)}
						onClick={noop}
						spacing="compact"
						tabIndex={-1}
					/>
				</span>
			</div>
			<div css={detailsBodyStyles}>
				<DetailField label={intl.formatMessage(messages.assignee)}>
					<PriyaAvatar />
					<Text>Priya Hansra</Text>
				</DetailField>
				<DetailField label={intl.formatMessage(messages.responders)}>
					<PaymentTeamAvatar />
					<Text>Payment Team</Text>
				</DetailField>
				<DetailField label={intl.formatMessage(messages.requestType)}>
					<IncidentIconTile size="small" />
					<Text>{intl.formatMessage(messages.incident)}</Text>
				</DetailField>
				<DetailField label={intl.formatMessage(messages.majorIncident)}>
					<Toggle
						id="jsm-playground-major-incident"
						isChecked={isMajorIncident}
						label={intl.formatMessage(messages.majorIncident)}
						onChange={() => setIsMajorIncident((previous) => !previous)}
					/>
				</DetailField>
				<DetailField label={intl.formatMessage(messages.collaborationChannels)} isMultiline>
					<div css={collaborationRowStyles}>
						<SlackMark label="Slack" />
						<Skeleton borderRadius={token('radius.small')} height={12} width={120} />
					</div>
					<div css={collaborationRowStyles}>
						<ZoomMark label="Zoom" />
						<Skeleton borderRadius={token('radius.small')} height={12} width={80} />
					</div>
				</DetailField>
			</div>
		</div>
	);
};

const DetailField = ({
	label,
	children,
	isMultiline = false,
}: {
	label: string;
	children: ReactNode;
	isMultiline?: boolean;
}) => (
	<div css={[detailFieldStyles, isMultiline && multilineDetailFieldStyles]}>
		<div css={detailLabelStyles}>
			<Text weight="medium">{label}</Text>
		</div>
		<div css={[detailValueStyles, isMultiline && multilineDetailValueStyles]}>{children}</div>
	</div>
);

const resolvedContentIn = keyframes({
	from: { opacity: 0, transform: 'translateY(8px)' },
	to: { opacity: 1, transform: 'translateY(0)' },
});

const ringSpin = keyframes({
	to: { transform: 'rotate(360deg)' },
});

const pageStyles = css({
	position: 'relative',
	display: 'flex',
	alignItems: 'flex-start',
	gap: token('space.300'),
	boxSizing: 'border-box',
	width: '100%',
	height: '100%',
	padding: token('space.500'),
	overflow: 'hidden',
	backgroundColor: token('elevation.surface'),
});

const leftColumnStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
	flex: '1 1 auto',
	minWidth: 0,
});

const leftCardsStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.300'),
});

const rightColumnStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
	width: '368px',
	flexShrink: 0,
});

const workItemHeaderStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.200'),
	minHeight: token('space.400'),
});

const workItemTitleStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.075'),
	flex: '1 1 auto',
	minWidth: 0,
});

const workItemCardStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
	boxSizing: 'border-box',
	width: '100%',
	padding: token('space.200'),
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.xlarge'),
	backgroundColor: token('elevation.surface'),
});

const cardHeaderStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.100'),
	minHeight: token('space.300'),
});

const cardHeaderTitleStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
	minWidth: 0,
});

const descriptionCardContentStyles = css({
	display: 'flex',
	flexDirection: 'column',
});

const descriptionDisclosureStyles = css({
	overflow: 'hidden',
	marginBlockStart: 0,
	opacity: 0,
	transition: `max-height ${PANEL_GROW_DURATION_MS}ms cubic-bezier(0.33, 0, 0.2, 1), margin-block-start ${PANEL_GROW_DURATION_MS}ms cubic-bezier(0.33, 0, 0.2, 1), opacity ${PANEL_ENTER_DURATION_MS}ms cubic-bezier(0.33, 0, 0.2, 1)`,
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
});

const descriptionDisclosureExpandedStyles = css({
	marginBlockStart: token('space.200'),
	opacity: 1,
});

const descriptionBodyStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
});

const paragraphStyles = css({
	margin: 0,
});

const alertSmartLinkStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.050'),
	boxSizing: 'border-box',
	height: token('space.250'),
	padding: token('space.025'),
	verticalAlign: 'middle',
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.small'),
});

const roundedBadgeStyles = css({
	display: 'inline-flex',
	overflow: 'hidden',
	borderRadius: token('radius.small'),
});

const impactedServiceSectionStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
});

const impactedServiceRowStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	boxSizing: 'border-box',
	minHeight: token('space.500'),
	padding: token('space.100'),
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.medium'),
});

const entityValueStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	flex: '1 1 auto',
	minWidth: 0,
});

const onCallStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
	flexShrink: 0,
});

const inlineControlStyles = css({
	display: 'inline-flex',
});

const inertControlStyles = css({
	display: 'inline-flex',
	pointerEvents: 'none',
});

const rovoCardFrameStyles = css({
	position: 'relative',
	// The 2px animation frame sits outside the card's 12px radius.
	borderRadius: '14px',
});

const rovoCardContentStyles = css({
	position: 'relative',
	zIndex: 1,
});

const rovoDisclosureStyles = css({
	overflow: 'hidden',
	maxHeight: 'var(--rovo-panel-max-height, none)',
	transition: `max-height ${PANEL_GROW_DURATION_MS}ms cubic-bezier(0.33, 0, 0.2, 1)`,
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
});

const rovoGlowStyles = css({
	position: 'absolute',
	inset: '-8px',
	borderRadius: token('radius.xxlarge'),
	background: `linear-gradient(135deg, ${wacToken('color.primary.saffron')}, ${wacToken(
		'color.extended.lime600',
	)}, ${wacToken('color.primary.blue')}, ${wacToken('color.extended.purple600')})`,
	filter: 'blur(12px)',
	opacity: 0,
	pointerEvents: 'none',
	transition: 'opacity 440ms cubic-bezier(0.33, 0, 0.2, 1)',
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
});

const rovoGlowVisibleStyles = css({
	opacity: 0.35,
});

const rovoRingStyles = css({
	position: 'absolute',
	inset: 0,
	zIndex: 0,
	overflow: 'hidden',
	padding: '2px',
	borderRadius: '14px',
	pointerEvents: 'none',
});

const rovoRingBitmapStyles = css({
	position: 'absolute',
	insetBlockStart: '50%',
	insetInlineStart: '50%',
	width: '1200px',
	height: '1200px',
	marginBlockStart: '-600px',
	marginInlineStart: '-600px',
	opacity: 0,
	background: `conic-gradient(${wacToken('color.primary.saffron')}, ${wacToken(
		'color.extended.lime600',
	)}, ${wacToken('color.primary.blue')}, ${wacToken('color.extended.purple600')}, ${wacToken(
		'color.primary.saffron',
	)})`,
	animationName: ringSpin,
	animationDuration: '6s',
	animationTimingFunction: 'linear',
	animationIterationCount: 'infinite',
	'@media (prefers-reduced-motion: reduce)': {
		animationName: 'none',
	},
});

const rovoRingVisibleStyles = css({
	opacity: 1,
});

const rovoInitialStyles = css({
	display: 'flex',
	alignItems: 'flex-start',
	gap: token('space.050'),
});

const rovoMarkWrapperStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	paddingBlock: token('space.025'),
	paddingInline: token('space.050'),
});

const rovoInitialContentStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
	flex: '1 1 auto',
	minWidth: 0,
});

const rovoCopyStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.050'),
});

const rovoActionsStyles = css({
	display: 'flex',
	flexWrap: 'wrap',
	gap: token('space.075'),
});

const rovoExpandedStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
});

const rootCausePanelStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
	boxSizing: 'border-box',
	width: '100%',
	padding: token('space.100'),
	borderRadius: token('radius.large'),
	backgroundColor: token('elevation.surface.sunken'),
});

const rootCausePanelHeaderStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.100'),
});

const rootCauseStepsStyles = css({
	display: 'flex',
	flexDirection: 'column',
	paddingBlock: token('space.075'),
});

const rootCauseStepStyles = css({
	display: 'flex',
	alignItems: 'stretch',
	gap: token('space.100'),
	minHeight: token('space.400'),
});

const stepRailColumnStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	paddingBlockStart: token('space.100'),
});

const stepRailStyles = css({
	width: token('border.width', '1px'),
	flex: '1 1 auto',
	backgroundColor: token('color.border'),
});

const pendingStepStyles = css({
	display: 'block',
	boxSizing: 'border-box',
	width: token('space.200'),
	height: token('space.200'),
	borderWidth: token('border.width.selected', '2px'),
	borderStyle: 'dashed',
	borderColor: token('color.icon.subtlest'),
	borderRadius: token('radius.full'),
});

const runningStepStyles = css({
	animationName: ringSpin,
	animationDuration: '1.6s',
	animationTimingFunction: 'linear',
	animationIterationCount: 'infinite',
	'@media (prefers-reduced-motion: reduce)': {
		animationName: 'none',
	},
});

const stepCopyStyles = css({
	display: 'flex',
	alignItems: 'center',
	flexWrap: 'wrap',
	gap: token('space.050'),
	minWidth: 0,
	minHeight: token('space.400'),
});

const resolvedContentStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
	animationName: resolvedContentIn,
	animationDuration: '440ms',
	animationTimingFunction: 'cubic-bezier(0.33, 0, 0.2, 1)',
	animationFillMode: 'both',
	'@media (prefers-reduced-motion: reduce)': {
		animationName: 'none',
	},
});

const rootCauseCardsStyles = css({
	display: 'flex',
	alignItems: 'stretch',
	width: '100%',
	overflow: 'hidden',
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.large'),
	backgroundColor: token('elevation.surface.raised'),
});

const rootCauseCardStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: token('space.100'),
	boxSizing: 'border-box',
	minWidth: 0,
	flex: '1 1 0',
	padding: token('space.200'),
	border: 0,
	backgroundColor: 'transparent',
	color: token('color.text'),
	font: token('font.body'),
	textAlign: 'start',
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
	'&:focus-visible': {
		outlineColor: token('color.border.focused'),
		outlineStyle: 'solid',
		outlineWidth: token('border.width.focused', '2px'),
		outlineOffset: '-2px',
	},
});

const rootCauseCardDividerStyles = css({
	borderInlineEndWidth: token('border.width', '1px'),
	borderInlineEndStyle: 'solid',
	borderInlineEndColor: token('color.border'),
});

const selectedRootCauseCardStyles = css({
	backgroundColor: token('color.background.selected'),
});

const highlightedRootCauseCardStyles = css({
	backgroundColor: token('color.background.neutral.subtle.hovered'),
});

const rootCauseTitleStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.100'),
	width: '100%',
});

const referenceTagStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.050'),
	paddingBlock: token('space.025'),
	paddingInline: token('space.050'),
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.small'),
	backgroundColor: 'transparent',
});

const rootCauseInsightStyles = css({
	boxSizing: 'border-box',
	width: '100%',
	padding: token('space.200'),
	borderRadius: token('radius.large'),
	backgroundColor: token('color.background.danger'),
	animationName: resolvedContentIn,
	animationDuration: '440ms',
	animationTimingFunction: 'cubic-bezier(0.33, 0, 0.2, 1)',
	animationFillMode: 'both',
	'@media (prefers-reduced-motion: reduce)': {
		animationName: 'none',
	},
});

const warningInsightStyles = css({
	backgroundColor: token('color.background.warning'),
});

const neutralInsightStyles = css({
	backgroundColor: token('color.background.neutral'),
});

const entityChipStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.050'),
	boxSizing: 'border-box',
	minWidth: 0,
	paddingBlock: token('space.025'),
	paddingInline: token('space.050'),
	verticalAlign: 'middle',
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.small'),
	backgroundColor: 'transparent',
});

const sourcesStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.075'),
});

const sourceLogosStyles = css({
	display: 'flex',
	alignItems: 'center',
});

const sourceLogoStyles = css({
	display: 'inline-flex',
	borderRadius: token('radius.small'),
	backgroundColor: 'transparent',
});

const sourceLogoOverlapStyles = css({
	marginInlineStart: '-6px',
});

const statusRowStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	minHeight: token('space.400'),
});

const statusDropdownStyles = css({
	position: 'relative',
	display: 'inline-flex',
	// This package version fails to calculate Popper coordinates inside the preview.
	// Keep ADS menu semantics and keyboard behavior, but anchor its inline popup to this wrapper.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& [data-testid="jsm-it-operations-status--content"]': {
		position: 'absolute !important',
		insetBlockStart: 'calc(100% + 4px) !important',
		insetInlineStart: '0 !important',
		transform: 'none !important',
	},
});

const statusMenuContentStyles = css({
	width: '240px',
});

const statusTransitionStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.150'),
});

const detailsPanelStyles = css({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	overflow: 'hidden',
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.xlarge'),
	backgroundColor: token('elevation.surface'),
});

const detailsHeaderStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.200'),
	boxSizing: 'border-box',
	minHeight: token('space.600'),
	paddingBlock: token('space.150'),
	paddingInline: token('space.200'),
	borderBlockEndWidth: token('border.width', '1px'),
	borderBlockEndStyle: 'solid',
	borderBlockEndColor: token('color.border'),
});

const detailsBodyStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.050'),
	paddingBlockEnd: token('space.100'),
});

const detailFieldStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	boxSizing: 'border-box',
	minHeight: token('space.500'),
	paddingBlock: token('space.050'),
	paddingInlineStart: token('space.200'),
	paddingInlineEnd: token('space.100'),
});

const multilineDetailFieldStyles = css({
	alignItems: 'flex-start',
});

const detailLabelStyles = css({
	width: '104px',
	flexShrink: 0,
});

const detailValueStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.075'),
	boxSizing: 'border-box',
	minWidth: 0,
	minHeight: token('space.400'),
	flex: '1 1 auto',
	paddingBlock: token('space.075'),
	paddingInline: token('space.100'),
});

const multilineDetailValueStyles = css({
	flexDirection: 'column',
	alignItems: 'stretch',
});

const collaborationRowStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.075'),
	minHeight: token('space.300'),
});
